import { ReactElement, ReactNode } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'

import Loader from '@/components/base/loader'
import SVGSprites from '@/components/base/svg-sprites'
import Header from '@/components/layout/header'
import ModalGallery from '@/components/sections/modal-gallery'
import { HeroLoaderProvider } from '@/contexts/hero-loader'
import { Store, StoreProvider } from '@/contexts/store/'
import { ThemeProvider } from '@/contexts/theme'
import { TransitionProvider } from '@/contexts/transition'
import { getPromiseFromFetch } from '@/utils/request'
import { API, GTM_ID, MOCK_DATA } from '@/constants'

// @ts-ignore - no type declarations for plyr CSS side-effect import
import '@/styles/fonts.css'
// @ts-ignore - no type declarations for plyr CSS side-effect import
import '@/styles/globals.css'
// @ts-ignore - no type declarations for plyr CSS side-effect import
import 'plyr/dist/plyr.css'

import '@/base/gsap'

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  menu,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <GoogleTagManager gtmId={GTM_ID} />
      <StoreProvider menu={menu} strings={strings}>
        <HeroLoaderProvider pageProps={pageProps}>
          <ThemeProvider theme={pageProps?.acf?.theme}>
            <Header pageProps={pageProps} />
            <TransitionProvider pageProps={pageProps}>
              {getLayout(<Component {...pageProps} />, pageProps)}
            </TransitionProvider>
            <ModalGallery />
            <Loader pageProps={pageProps} />
            <SVGSprites />
          </ThemeProvider>
        </HeroLoaderProvider>
      </StoreProvider>
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const lang = appContext.router.locale

  const { data: menuData } = MOCK_DATA
    ? { data: require(`@/data/menu_locations.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.MENU_LOCATIONS}?lang=${lang}`
      })

  const { data: optionsData } = MOCK_DATA
    ? { data: require(`@/data/options.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.STRINGS}?lang=${lang}`
      })

  return {
    ...appProps,
    strings: optionsData,
    menu: Object.keys(menuData).length > 0 ? menuData : {}
  }
}
